export interface TechInfo {
  label: string;
  description: string;
  swatch: string;
}

export interface TechBadgeColors {
  bg: string;
  fg: string;
  border: string;
}

/**
 * Canonical form of a technology string. Exported because every consumer must
 * normalise identically — the technology arrives from two producers that
 * disagree on case (the host's `setMultiTechAreaGroups` payload sends
 * lowercase, the editor's own derivation returns capitalised), and a
 * case-sensitive comparison anywhere reintroduces the original bug.
 */
export const normaliseTechnology = (tech: string | null | undefined): string => (tech ?? '').trim().toLowerCase();

const normalise = normaliseTechnology;

const titleCase = (tech: string): string => {
  const words = tech.trim().replace(/[-_]+/g, ' ').trim();
  if (!words) {
    return '';
  }
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
};

const TECH_INFO: Record<string, TechInfo> = {
  dtf: {
    label: 'DTF transfer',
    description: 'Vivid colours, smooth feel. Best for full-colour artwork.',
    swatch: '#e3f2fd',
  },
  dtg: {
    label: 'Direct-to-garment',
    description: 'Soft hand feel printed straight onto the fabric.',
    swatch: '#f3e5f5',
  },
  embroidery: {
    label: 'Embroidery',
    description: 'Stitched thread. Best for logos and simple shapes.',
    swatch: '#fff3e0',
  },
  sublimation: {
    label: 'Sublimation',
    description: 'Permanently dyes the fabric. Best for polyester.',
    swatch: '#e8f5e9',
  },
};

const TECH_BADGE_COLORS: Record<string, TechBadgeColors> = {
  sublimation: { bg: '#e8f5e9', fg: '#1b5e20', border: '#a5d6a7' },
  embroidery: { bg: '#fff3e0', fg: '#e65100', border: '#ffb74d' },
  dtf: { bg: '#e3f2fd', fg: '#0d47a1', border: '#90caf9' },
  dtg: { bg: '#f3e5f5', fg: '#4a148c', border: '#ce93d8' },
};

const TECH_BADGE_LABELS: Record<string, string> = {
  dtf: 'DTF',
  dtg: 'DTG',
  embroidery: 'Embroidery',
  sublimation: 'Sublimation',
};

// Neutral, so a technology with no palette of its own is never painted in
// another one's colours — the badge is how a tile says which tech it prints.
const DEFAULT_TECH_BADGE_COLOR: TechBadgeColors = { bg: '#eeeeee', fg: '#424242', border: '#bdbdbd' };

export function getTechInfo(tech: string | null | undefined): TechInfo {
  const known = TECH_INFO[normalise(tech)];
  if (known) {
    return known;
  }
  return { label: titleCase(tech ?? ''), description: '', swatch: '#eeeeee' };
}

/**
 * Short label for the bottom-strip badge. Print areas are named by the
 * customer, so the trailing token of a spread name is not always a
 * technology (`14x16-b` yields `b`). Recognised technologies get their
 * canonical name; anything else is shown as written rather than guessed at.
 */
export function getTechBadgeLabel(tech: string | null | undefined): string {
  return TECH_BADGE_LABELS[normalise(tech)] ?? titleCase(tech ?? '');
}

export function getTechBadgeColors(tech: string | null | undefined): TechBadgeColors {
  return TECH_BADGE_COLORS[normalise(tech)] ?? DEFAULT_TECH_BADGE_COLOR;
}

export function isSameTechnology(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = normalise(a);
  const right = normalise(b);
  return left !== '' && left === right;
}
